// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-02-07 10:18:45 +0100 (Fri, 07 Feb 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: o.powroznik $
// Revision    : $Rev: 150036 $
// Purpose     : login/logout parts
// =============================================================================
// ---Third-party imports

import React, {useState, useEffect} from 'react'
import {Icon, Header, Form, Segment, Button, Input, Message, Image} from 'semantic-ui-react'

// ---Framework imports
import {get} from 'ldash'
import {Actions} from 'uix/registries'
import {Part} from 'uix/parts'
import log from 'uix/logging'
import {usePersistent, useActions, useSelector, useCallApi} from 'uix/hooks'
import {HeaderSettingsIcon} from 'uix/components'
import {WebAuthNDiag} from './components'
import connect from 'uix/connect'
import {queryWebauthn, setUser} from './actions'
import {VIcon, vicon} from 'graphics'
import {Sel} from 'uix/registries'

// ---Local imports
import styles from './auth.scss'
import clsx from 'clsx'

// ---Exports

const RecoverForm = ({username, onClose}) => {
  let i18n = useSelector(Sel.i18n)
  let {callApi: recoverPasswordMail} = useCallApi('user/recoverPasswordMail')

  let [email, setEmail] = useState('')
  let [recoverFailure, setRecoverFailure] = useState('')
  let [mailSuccess, setMailSuccess] = useState(false)

  let sendMail = () => {
    setRecoverFailure('')
    recoverPasswordMail({login: username, email}, (data, error) => {
      setMailSuccess(!!data?.mail_send)
      if (data?.recover_error) {
        setRecoverFailure(data?.recover_error)
      }
    })
  }

  let isValidEmail = email => {
    let emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
  }

  return (
    <Form
      className={clsx('attached fluid segment', styles.formContainer)}
      onSubmit={() => sendMail()}
      error={!!recoverFailure}
      success={mailSuccess}
    >
      <div className={styles.headerForget} style={{}}>
        <Button secondary icon="arrow left" onClick={onClose} />
        <h2>{i18n.get('resetpw')}</h2>
      </div>
      <p style={{margin: '1rem 0 ', textAlign: 'center'}}>
        Geben Sie die mit <b>{username}</b> verbundene E-Mail Adresse ein:
      </p>
      <Input
        name="email"
        value={email}
        iconPosition="left"
        placeholder={i18n.get('email', 'Email')}
        onChange={(e, {value}) => setEmail(value)}
        required
        fluid
        ref={input => (input ? input.focus() : null)}
      >
        <VIcon name="at" />
        <input />
      </Input>
      <div className={styles.buttonArea}>
        <div>
          <Message size="mini" error>
            <Icon name="exclamation" />
            {recoverFailure}
          </Message>
          <Message size="mini" success>
            Success
          </Message>
        </div>
        <Button
          disabled={!isValidEmail(email)}
          className={styles.submit}
          fluid
          content="Neues Passwort anfordern"
        />
      </div>
      <div></div>
    </Form>
  )
}
export const AuthLogin = () => {
  let runLogin = useActions(Actions.runLogin)
  let apiurl = useSelector(Sel.apiurl)
  let url = apiurl.href(`/favicon.ico`)
  let urlStr = `${apiurl.baseUrl('').slice(8)}`

  let [username, setUsername] = usePersistent('username', '')
  let [showPassword, setShowPassword] = usePersistent('showPassword', true)
  let [password, setPassword] = useState('')

  let sel = useSelector(root => ({
    loginFailure: root.meta.loginFailure,
    user: root.meta.user,
    i18n: root.meta.i18n,
    request: root.meta.request,
    webauthn: root.meta.webauthn,
    email_on: root.config.instance.email.on,
  }))

  let webauthnAvailable = sel.webauthn.available && sel.webauthn.available !== 'LOADING'
  let webauthnError = sel.webauthn.state && sel.webauthn.state.startsWith('ERROR_')
  let usePassword = showPassword || webauthnError

  let noInsufficientPermissionHack =
    sel.request.classname == 'test' && sel.request.tabname == 'auth'

  let failure = null

  // show recover form instead of login form
  let [recoverMode, setRecoverMode] = useState(false)
  let onPasswordRecoverClick = null
  if (sel.email_on) {
    onPasswordRecoverClick = () => setRecoverMode(true)
  }

  if (sel.loginFailure) {
    failure = (
      <Message className={styles.failure} size="mini" error>
        <Icon name="exclamation" />
        {get(sel.i18n, sel.loginFailure.reason, sel.loginFailure.reason)}
      </Message>
    )
  } else if (sel.user.name && !noInsufficientPermissionHack) {
    // if we have a valid login, assume it's a permission problem
    // we also need to handle i18n manually
    failure = (
      <Message className={styles.failure} size="mini" error>
        <Icon name="exclamation" />
        {sel.i18n['msg_insufficient_permission']}
      </Message>
    )
  }
  let passwordToggle = value => {
    setShowPassword(value)
    // auto magic, run non-pw login immediately
    if (!value) {
      runLogin(null)
    }
  }

  let runLoginHandler = () => {
    if (password === undefined) {
      password = usePassword ? password : null
    }
    runLogin({
      username: username,
      password,
    })
  }

  let content = (
    <Form
      className={clsx('attached fluid segment', styles.formContainer)}
      onSubmit={() => runLoginHandler()}
    >
      <Image
        src={url}
        onError={e => {
          e.target.onError = null //prevents looping
          e.target.src = '' //fallback image
        }}
        style={{height: '3rem'}}
      />
      <Header className={styles.header} textAlign="center" as="h2">
        {get(sel.i18n, 'welcomeBack', 'Welcome back')}!
        <Header.Subheader size="tiny">{urlStr}</Header.Subheader>
      </Header>
      <Form.Field className={styles.inputs}>
        <Input
          name="username"
          autoComplete="username"
          value={username}
          iconPosition="left"
          placeholder={get(sel.i18n, 'username', 'User')}
          onChange={(e, {value}) => setUsername(value)}
          required
          ref={input => (input && (!username || !usePassword) ? input.focus() : null)}
        >
          <VIcon name="user" />
          <input />
        </Input>
      </Form.Field>
      {usePassword ? (
        <PasswordField
          username={username}
          isFocused={Boolean(username)}
          placeholder={get(sel.i18n, 'password', 'Password')}
          onChange={password => setPassword(password)}
          showAlways={showPassword || webauthnError}
          onPasswordRecoverClick={onPasswordRecoverClick}
        />
      ) : null}
      {!showPassword || webauthnAvailable ? (
        <Button.Group fluid>
          <Button
            icon="keyboard outline"
            style={{paddingRight: '1em'}}
            positive={usePassword}
            onClick={() => passwordToggle(true)}
          />
          <Button.Or />
          <Button
            positive={!usePassword}
            icon={{...vicon('fingerprint'), style: {paddingLeft: '0.3em'}}}
            onClick={() => passwordToggle(false)}
          />
        </Button.Group>
      ) : null}
      <div className={clsx(styles.buttonArea, failure && styles.failure)}>
        <div>{failure}</div>
        <Button className={styles.submit} fluid animated="fade">
          <Button.Content visible>{get(sel.i18n, 'doLoginButtonLabel', 'Log in')}</Button.Content>
          <Button.Content hidden>
            <VIcon name="sign-in"></VIcon>
          </Button.Content>
        </Button>
      </div>
    </Form>
  )

  if (recoverMode) {
    content = <RecoverForm username={username} onClose={() => setRecoverMode(false)} />
  }

  return (
    <div className={styles.container}>
      <div className={styles.authContainer}>{content}</div>
    </div>
  )
}

let PasswordField = connect(
  'LoginPasswordField',
  {storeMap: 'meta.webauthn', actionMap: {queryWebauthn}},
  ({
    isFocused,
    placeholder,
    webauthn,
    username,
    queryWebauthn,
    onChange,
    showAlways,
    onPasswordRecoverClick,
  }) => {
    let [password, setPassword] = useState('')
    let i18n = useSelector(Sel.i18n)
    useEffect(() => {
      if (username) {
        queryWebauthn(username)
      }
    }, [username])

    let the_input = null
    useEffect(() => {
      if (isFocused && the_input) {
        the_input.focus()
      }
    }, [showAlways])

    let change = value => {
      onChange(value)
      setPassword(value)
    }

    if (!showAlways && webauthn.available !== true) {
      return null
    }

    return (
      <Form.Field style={{position: 'relative'}}>
        <Input
          name="password"
          autoComplete="current-password"
          value={password}
          type="password"
          iconPosition="left"
          placeholder={placeholder}
          onChange={(e, {value}) => change(value)}
          required
          ref={input => (the_input = input)}
        >
          <VIcon name="lock" />
          <input />
        </Input>
        {onPasswordRecoverClick && (
          <a
            style={{
              cursor: 'pointer',
              paddingTop: '.3em',
              textAlign: 'right',
              width: '100%',
              display: 'block',
              fontSize: '.85em',
            }}
            onClick={onPasswordRecoverClick}
          >
            {i18n.get('resetpw')} ?
          </a>
        )}
      </Form.Field>
    )
  }
)

export class WebAuthN extends Part {
  _render() {
    return (
      <>
        <Message negative>
          <a href="https://github.com/Yubico/python-fido2/issues/52">
            See: Windows TPM attestation probs, adapt to pywebauthn
          </a>
        </Message>

        <Segment>
          <HeaderSettingsIcon
            content="WebAuthN"
            subheader="test registration and login procedure"
            icon={vicon('fingerprint')}
          />
          <WebAuthNDiag />
        </Segment>
      </>
    )
  }
}
